import {
  type ProductAction,
  type ProductActionResponse,
  type ProductActionTier,
} from '../types';
import {
  DEFAULT_PRODUCT_ACTION_COLOR,
  PRODUCT_ACTION_TYPES_MAP,
} from '../constants.d';
import { Price } from '~/utils';
import { productActionDateTillSerializer } from '~/domains/product';

function getActionColor(data: ProductActionResponse): string {
  if (data.icon_type === 'CUSTOM') {
    return data.icon_color !== undefined && data.icon_color !== ''
      ? data.icon_color
      : DEFAULT_PRODUCT_ACTION_COLOR;
  }

  return (
    PRODUCT_ACTION_TYPES_MAP[data.icon_type]?.color ??
    DEFAULT_PRODUCT_ACTION_COLOR
  );
}

function getActionTiers(data: ProductActionResponse): ProductActionTier[] {
  if (data.action_product_price_type !== 'QTY_DISCOUNT') return [];

  const tiers: ProductActionTier[] = [];

  for (let index = 1; `qty${index}` in data; index++) {
    const quantity = Number(data[`qty${index}`]);
    const price = data[`estimated_price${index}`];

    if (quantity > 0 && price !== undefined && price !== null && price !== '') {
      tiers.push({ quantityFrom: quantity, price: new Price(price) });
    }
  }

  return tiers
    .sort((a, b) => a.quantityFrom - b.quantityFrom)
    .map((tier, index, sorted) => ({
      ...tier,
      quantityTo:
        index < sorted.length - 1
          ? sorted[index + 1].quantityFrom - 1
          : undefined,
    }));
}

export default function productActionSerializer(
  data: ProductActionResponse,
): ProductAction {
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    iconType: data.icon_type,
    iconColor: data.icon_color,
    iconLabel: data.icon_label,
    iconVisible: data.icon_visible,
    hoverText: data.hover_text,
    color: getActionColor(data),
    label: PRODUCT_ACTION_TYPES_MAP[data.icon_type]?.label ?? data.icon_type,
    dateTill: productActionDateTillSerializer(data),
    actionProductPriceType: data.action_product_price_type,
    quantity1: data.qty1,
    quantity2: data.qty2,
    quantity3: data.qty3,
    priceFixed1:
      data.price_fixed1 !== undefined ? new Price(data.price_fixed1) : '',
    priceFixed2:
      data.price_fixed2 !== undefined ? new Price(data.price_fixed2) : '',
    priceFixed3:
      data.price_fixed3 !== undefined ? new Price(data.price_fixed3) : '',
    discount1: data.discount1 !== undefined ? new Price(data.discount1) : '',
    discount2: data.discount2 !== undefined ? new Price(data.discount2) : '',
    discount3: data.discount3 !== undefined ? new Price(data.discount3) : '',
    discountPercent1: data.discount_percent1,
    discountPercent2: data.discount_percent2,
    discountPercent3: data.discount_percent3,
    estimatedPrice1:
      data.estimated_price1 !== undefined
        ? new Price(data.estimated_price1)
        : '',
    estimatedPrice2:
      data.estimated_price2 !== undefined
        ? new Price(data.estimated_price2)
        : '',
    estimatedPrice3:
      data.estimated_price3 !== undefined
        ? new Price(data.estimated_price3)
        : '',
    tiers: getActionTiers(data),
  };
}
